import React, { Component } from "react";
import PropTypes from "prop-types";
import Divider from "@material-ui/core/Divider";
import * as Theme from "../../../app/Theme";
import TLabel from "../../common/TLabel";
import Link from "@material-ui/core/Link";
import * as HLConst from "../NonVisual/HLConst";
import { resourceClick, URL_Click } from "../NonVisual/HLUtil";
import TAccordion from "../../common/TAccordian";

class HLFAQsHelpForm extends Component {
    static propTypes = {
        onPageChange: PropTypes.func,
    };

    static defaultProps = {
        onPageChange: () => console.log("onPageChange"),
    };

    state = {};

    //==================================================================================================================
    //
    //                                              Event Handlers
    //
    //==================================================================================================================

    onPageChange = (pageID) => {
        this.props.onPageChange(pageID);
    };

    //==================================================================================================================
    //
    //                                                 Render
    //
    //==================================================================================================================

    renderFAQsAccordion = () => {
        const howItWorksDiv = (
            <div
                style={{
                    ...Theme.textFontStyle,
                }}
            >
                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    What is the basic premise behind the H-LiST approach?
                </p>

                <p>
                    H-LiST was developed to respond to a clear need in the humanitarian community: effectiveness of
                    health interventions and their cost-effectiveness in humanitarian settings are essential given the
                    increasing number, magnitude and complexity of emergencies combined with scarce and precious
                    funding. However, there was no guidance on which interventions can save the largest number of
                    persons’ lives at the most effective cost given available resources, in a particular humanitarian
                    setting.
                </p>

                <p>
                    Using modelled results from an adapted version of the&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Lives Saved Tool (LiST)
                    </Link>
                    , H-LiST walks users through a&nbsp;
                    <span
                        style={{
                            textDecoration: "underline",
                        }}
                    >
                        seven-step process
                    </span>
                    &nbsp;to identify the maternal and child health, nutrition and water, sanitation and hygiene (WASH)
                    interventions, at specified coverage levels, that can save the most amount of maternal and
                    children’s lives in a particular humanitarian setting. The approach also includes guidance on
                    developing and costing context-specific activities to achieve the specified intervention coverage
                    levels.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    How does H-LiST prioritize interventions for my specific setting?
                </p>

                <p>
                    The H-LiST approach is powered by an adaptation of the&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Lives Saved Tool (LiST)
                    </Link>
                    , an evidence-based tool widely used to model the effect of scaling up proven public health
                    interventions with established effectiveness. Similar to LiST, the primary inputs for H-LiST
                    modeling are coverage of key interventions, intervention effectiveness for specific causes of death,
                    and contextual factors which may mediate the influence of health interventions (e.g.,
                    undernutrition, micronutrient deficiency). LiST calculates changes in cause-specific mortality based
                    on:
                </p>

                <ul>
                    <li>
                        User-defined intervention coverage change (for a group or package of related interventions: from
                        40% to 60%, 60% to 80%, 80% to 90%, and 90% to 95%, over a one year period)
                    </li>

                    <li>
                        Affected fraction (proportion of cause-specific deaths that can be averted by a specific
                        intervention) and intervention effectiveness for that cause (taken from the global evidence of
                        systematic reviews, meta-analyses, Delphi estimations, and randomized control trials)
                    </li>

                    <li>
                        Percentage of cause-specific mortality sensitive to that intervention (also from the global
                        evidence)
                    </li>
                </ul>

                <p>
                    Impact is quantified in this linear, deterministic model as the number of lives saved or reduction
                    in cause-specific deaths attributable to expanded coverage of interventions. More details on the
                    LiST modelling can be found in the technical note.
                </p>

                <p>
                    With the prioritized interventions results from LiST known, users then identify context-specific
                    incremental activities to scale-up coverage of those interventions, estimate the incremental cost of
                    those activities and then assess the feasibility of improving coverage of each intervention given
                    both incremental cost (considering available resources) and implementation-related challenges (e.g.,
                    stockouts). Then the user makes a final decision about which interventions should be prioritized for
                    his/her setting.
                </p>
            </div>
        );

        const inclIntervsDiv = (
            <div>
                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Why does H-LiST only include maternal, child and neonatal, nutrition and WASH interventions?
                </p>

                <p>
                    The interventions in&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        LiST
                    </Link>
                    &nbsp;(which powers H-LiST) impact the major causes of&nbsp;
                    <span
                        style={{
                            textDecoration: "underline",
                        }}
                    >
                        neonatal, under-five, and maternal mortality and nutrition outcomes in low and middle income
                        countries
                    </span>
                    . These are also the interventions for which high-quality evidence from systematic reviews,
                    meta-analyses, Delphi method and randomized control trials is available about the intervention’s
                    effectiveness. Currently, it is not possible to use LiST to model intervention impact on chronic
                    diseases, neglected tropical diseases, injuries, and adolescent health.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    What if I am implementing interventions beyond those included in H-LiST?
                </p>

                <p>
                    You can only use H-LiST to prioritize amongst the included interventions. Unfortunately, other
                    interventions are not currently included in&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        LiST
                    </Link>
                    , which powers the H-LiST approach. You could, however, use the same costing framework for
                    considering the cost of activities to improve coverage of other interventions not included in
                    H-LiST.
                </p>
            </div>
        );

        const dataReqDiv = (
            <div>
                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    What data am I required to provide to use the H-LiST approach?
                </p>

                <p>
                    To allow users to easily generate results without requiring extensive data inputs, users need only
                    provide:
                </p>

                <ul>
                    <li>Country</li>

                    <li>Baseline and target coverage rates, by intervention area (choice of five options)</li>

                    <li>Activities to improve coverage of priority interventions</li>

                    <li>
                        Unit costs of inputs required to implement activities to scale-up coverage (exact requirements
                        depend on the chosen activities)
                    </li>
                </ul>

                <p>
                    The first two pieces are used as part of step 2, while the definitions of activities and their costs
                    comes later, as part of steps 3 and 4.
                </p>

                <p>
                    The intervention areas represent a package of related interventions (pregnancy, childbirth and
                    periconceptual, immunization (EPI), nutrition, OPD and community outreach and WASH). For each area
                    you wish to see results for, you must choose from&nbsp;
                    <span
                        style={{
                            textDecoration: "underline",
                        }}
                    >
                        pre-defined baseline and target coverage rates
                    </span>
                    : 40% to 60%, 60% to 80%, 80% to 90% and 90% to 95%. This selection is best made by looking at the
                    current coverage of the interventions in each intervention area (based on HIS or other program
                    monitoring data) in your particular setting over the last year and choosing the scale-up scenario
                    that best reflects the current coverage of the majority of interventions in that intervention group.
                    It may be that coverage rates in your setting for interventions in a particular group fall into more
                    than one scale-up scenario; for example, within the EPI intervention group, coverage of BCG may be
                    93%, meaning the 90% to 95% coverage scale-up scenario would be most appropriate, but Measles
                    coverage is 75%, meaning the 60% to 80% coverage scale-up scenario is a better fit for this
                    intervention. You may need to make difficult choices to select the coverage scale-up scenario that
                    best fits the majority of, but not all, interventions. Optional user inputs which can further
                    customize the results but are not required are:
                </p>

                <p>Optional user inputs which can further customize the results but are not required are:</p>

                <ul>
                    {/*<li>*/}
                    {/*Causes of death for children and/or women*/}
                    {/*</li>*/}

                    <li>
                        Population structure (percentage distribution of males and females into the age groups of 0-4,
                        5-14, 15-64, 64+ years); the&nbsp;
                        <Link
                            onClick={() => URL_Click(HLConst.LiST_URL)}
                            style={{
                                color: Theme.HL_LinkBlue,
                                cursor: "pointer",
                            }}
                            underline={"hover"}
                        >
                            LiST
                        </Link>
                        &nbsp;results will be based on the population structure of the user-selected country (coming
                        from the 2019 Revision of World Population Prospects (2019 WPP report)), unless the user
                        provides data to override this default
                    </li>

                    <li>
                        Stunting in 6-59 month olds; the LiST results will be based on stunting data from national-level
                        household surveys like DHS and MICS, unless the user provides data to override this default
                    </li>

                    <li>
                        Wasting (global acute malnutrition (GAM)) in 6-59 month olds; the LiST results will be based on
                        wasting data from national-level household surveys like DHS and MICS, unless the user provides
                        data to override this default
                    </li>
                </ul>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Why do I not have to input population size of the humanitarian-affected population? Surely that will
                    change the number of lives saved.
                </p>

                <p>
                    Although H-LiST uses the total population numbers for the user-selected country, results are
                    presented in terms of percentage of lives saved (and not the actual numbers of lives saved), making
                    the total population size irrelevant. For this reason, it is not changeable by users. However, the
                    population breakdown (males/females and by age groups) can be replaced or edited by users, and this
                    may affect the percentages of lives saved.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Do you provide a definition of each intervention and how coverage should be measured? What if I
                    measure coverage of my intervention differently?
                </p>

                <p>
                    Please see the LiST user manual for a definition of each intervention. We recognize that the
                    coverage of some interventions may be measured differently in some humanitarian settings, compared
                    to what is described in the manual. However, if the intervention itself is not significantly
                    different, results should still be valid. If you feel the intervention itself is not comparable in
                    your humanitarian setting, you may choose to adjust the prioritization results.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Does H-LiST provide a database of activities proven to increase coverage of the interventions
                    included in LiST?
                </p>

                <p>
                    No. Interventions that are effective at increasing coverage in one setting may not work in other
                    settings. Activities to increase coverage in your setting are best discussed by those working in
                    that humanitarian context, who know what has been tried, what the main bottlenecks are, and what new
                    ideas might be considered.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Does H-LiST provide any built-in costing data for me to access?
                </p>

                <p>
                    No. Because costs are so context- and organization-specific, and will also depend on the activities
                    the user chooses to scale-up coverage of the priority interventions, we have not provided any
                    default data.
                </p>
            </div>
        );

        const costCompDiv = (
            <div>
                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Does H-LiST provide any built-in costing data for me to access?
                </p>

                <p>
                    No. Because costs are so context- and organization-specific, and will also depend on the activities
                    the user chooses to scale-up coverage of the priority interventions, we have not provided any
                    default data.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Will H-LiST help me generate a program budget, or a budget for a funding application, for example?
                </p>

                <p>
                    No. The costing guidance included in H-LiST helps users identify only the incremental, or
                    additional, costs of the activities identified to help scale-up coverage. As such, these are on-top
                    of existing budgets. The approach does not generate an overall budget, but only a budget for the new
                    activities identified.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Can H-LiST take into account the cost of capital items (such as WASH infrastructure), where the
                    investment is made today but the pay-off lasts for many years?
                </p>

                <p>
                    Yes. Our guidance notes that capital items, such as vehicles, buildings or WASH infrastructure, may
                    appear more “expensive” than other activities, but the lifetime of the investment must be taken into
                    consideration. Many seemingly less expensive activities, such as community sensitization, must
                    happen repeatedly, and therefore included in each year’s budget. Capital purchases, on the other
                    hand, are one-off expenses which will be in use for multiple years. For WASH infrastructure in
                    particular, it’s also relevant to note that these investments may help avert treatment-related costs
                    in the future. For example, improved water sources may reduce the incidence of watery diarrhea.
                    Thinking about the approximate annual cost of capital items makes it easier to compare them with
                    recurrent expenditures. However, the capital expenditures of course still require available budget
                    up front in order to be able to make the one-time purchase. These considerations should be taken
                    into account when making a final decision on the interventions to scale-up and the activities to
                    pursue.
                </p>
            </div>
        );

        const resultsDiv = (
            <div>
                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    What results does the H-LiST approach produce?
                </p>

                <p>Results of the seven steps of the H-LiST approach include:</p>

                <ul>
                    <li>
                        A prioritized list of maternal, newborn and child health (MNCH), nutrition and WASH
                        interventions that can save the most lives in a particular context, given current coverage
                        levels and considering incremental costs and feasibility
                    </li>

                    <li>A list of activities to increase coverage of the prioritized interventions</li>
                </ul>

                <p>
                    The prioritized list of MNCH, nutrition and WASH interventions comes from&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        LiST
                    </Link>
                    , and is available separately for women and children as well as in combination. The exact number of
                    lives saved is not generated since H-LiST does not use specific population sizes. Instead, a
                    percentage of the overall lives saved is displayed.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Why can’t I input the population affected by the humanitarian emergency to see the actual number of
                    lives saved, and not just the prioritization of interventions vis a vis one another?
                </p>

                <p>
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        LiST
                    </Link>
                    &nbsp;was not designed to estimate lives saved in small, rapidly changing populations such as those
                    seen in some humanitarian settings, but instead for large, stable populations such as districts,
                    regions and countries. Therefore, the exact number of lives saved cannot be accurately determined.
                    Instead, we use the ranking of interventions in terms of lives saved at certain coverage levels
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Will H-LiST allow me to make decisions on other programs beyond MNCH/WASH/nutrition?
                </p>

                <p>
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        LiST
                    </Link>
                    , and as a result also H-LiST, does not currently include interventions beyond MNCH/WASH/nutrition.
                    Therefore, decisions about other programs (including types of interventions and costing) beyond
                    these areas should not be considered.
                </p>
            </div>
        );

        const usesDiv = (
            <div>
                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Who is the H-LiST approach designed for?
                </p>

                <p>
                    The H-LiST approach can be used by governments, organizations and donors working in low- and
                    middle-income, acute and protracted humanitarian settings. It can be used for program planning
                    and/or advocacy for additional resources for priority interventions. H-LiST can also be used by
                    donors to shape guidance for implementing partners.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Does the H-LiST approach apply only to camps/settlements?
                </p>

                <p>
                    No, the H-LiST approach can be used in all types of humanitarian settings and with any population
                    affected by humanitarian emergencies, in any phase of an emergency. However, in non-camp settings
                    (refugees or IDPs) because of integration with refugees and the multitude of ways they can use
                    services, users may have not access to reliable data on the coverage of the included interventions
                    for the humanitarian population only.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Is the H-LiST approach only for refugees?
                </p>

                <p>
                    No, the H-LiST approach can be used in all types of humanitarian settings and with any population
                    affected by humanitarian emergencies, and in any phase of an emergency.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Where has the H-LiST approach been used?
                </p>

                <p>
                    The H-LiST approach was piloted in conjunction with the&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.UNHCR_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        United Nations High Commissioner for Refugees (UNHCR)
                    </Link>
                    &nbsp;and their partners in Uganda in September 2019. It has also been shared with UNHCR
                    headquarters and Kenya offices, headquarters of various UN agencies in Geneva and New York and NGOs
                    working in the humanitarian space.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    For what purposes is the H-LiST approach not appropriate?
                </p>

                <p>
                    There are also some limits for which the H-LiST approach can be used. The H-LiST approach cannot be
                    used to allocate funding between RMNCH and other health/non-health areas. It is NOT a justification
                    to stop provision of certain interventions. It does NOT offer a fully costed service delivery
                    packages. It is NOT a tool or model to calculate the total number of lives saved in a humanitarian
                    context, nor can it be used to measure the impact of interventions on morbidity or quality of life.
                    Finally, it is not appropriate to use the H-LiST approach in high-income settings.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    How is H-LiST different from “regular” LiST?
                </p>

                <p>
                    In assessing the suitability of&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        LiST
                    </Link>
                    &nbsp;for humanitarian contexts, we identified certain components or assumptions that needed to be
                    adjusted to make the tool better suited to humanitarian settings. Please see the technical note for
                    a more detailed description of adjustments made to LiST.
                </p>
            </div>
        );

        const limitationsDiv = (
            <div>
                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Does the H-LiST approach take into account quality of life (morbidity) or dignity?
                </p>

                <p>
                    Powered by the&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Lives Saved Tool (LiST)
                    </Link>
                    , the H-LiST approach only prioritizes interventions based on maternal and child lives saved. Other
                    important considerations, such as quality of life or lives saved in men or other age groups, are not
                    captured. The impact of interventions which have primarily a quality of life impact, such as
                    promotion of menstrual hygiene, therefore, is not captured in the LiST results (step 2). Quality of
                    life and dignity are extremely important components in humanitarian settings, but are not addressed
                    by this tool. Although the model originated to examine mortality as the main outcome of interest, a
                    wider array of outputs have been added and are now available. These include child health (e.g.,
                    stunting and wasting, disease-specific incidence such as diarrhea or pneumonia cases) and maternal
                    health (e.g., anemia). Some morbidity outcomes are represented by undernutrition or adverse birth
                    outcomes (e.g., status as preterm or small-for-gestational age) which influence other outcomes such
                    as mortality further “downstream.”
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    How does LiST/H-LiST deal with epidemics?
                </p>

                <p>
                    The&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        LiST
                    </Link>
                    &nbsp;model does not have the inherent capability to predict the occurrence or response to a disease
                    outbreak. The estimates of cause-specific mortality by year that we apply at baseline, which come
                    from the Maternal and Child Epidemiology Estimation (MCEE) project, assume that trends are
                    relatively stable over time so there would not be any emergent crisis “automatically” programmed as
                    part of a cycle. The number of measles deaths, for example, are going to shift slightly based upon
                    coverage, but there will not be any spikes observed or modeled. The model DOES have the ability to
                    model complex patterns of vaccine delivery through catch-up campaigns, etc., but this does not
                    relate to and is not driven by any significant increase that occurs like during a disease outbreak.
                    Therefore, epidemics are not included in this model. Consequently, specific interventions such as
                    mass vaccination for measles or oral cholera vaccine have not been included in this model. They
                    should be carried out according to existing standards (e.g., Sphere standards).
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    How is quality of care of the included interventions taken into account?
                </p>

                <p>
                    For antenatal and childbirth interventions,&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        LiST
                    </Link>
                    &nbsp;has the ability to adjust for quality of care, but this is not currently available in the
                    results that are provided as part of H-LiST. The rationale is that the ability to estimate readiness
                    (e.g., availability of supplies and trained staff) as a proxy for quality of care to calculate
                    intervention coverage would require health systems information or health facility data which may not
                    be readily available or routinely collected in a humanitarian setting.
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    What are some of the limitations of the H-LiST approach?
                </p>

                <p>
                    Like any model, the&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Lives Saved Tool (LiST)
                    </Link>
                    , which powers the H-LiST approach, has some limitations. Many of these limitations are due to the
                    paucity of data and proxies which must be applied to estimate impact on cause-specific mortality.
                    For certain interventions (e.g. supplementation), quantifying impact for discrete population
                    subgroups is challenging and for others (e.g. surgery), randomized controlled trials or field tests
                    may not be ethical or feasible. Interventions such as cholera vaccine which may be delivered to
                    control emergent outbreaks are not currently included. Despite consultations with experts and
                    iterative rounds of design to incorporate information available from humanitarian settings into an
                    integrated approach, some aspects of the LiST model could not be adapted to humanitarian contexts.
                    Issues related to the collection of mortality and population by age data, population movements, and
                    other challenges were explored and may be addressed further in subsequent versions. Please see our
                    technical note for more information.
                </p>
            </div>
        );

        const moreInfoDiv = (
            <div>
                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    What other resources are available to help me with the H-LiST approach?
                </p>

                <p>
                    Please see our&nbsp;
                    <Link
                        onClick={() => resourceClick(HLConst.techNotePDF)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        technical note
                    </Link>
                    ,&nbsp;
                    <Link
                        onClick={() => resourceClick(HLConst.guidanceDocPDF)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        full guidance document
                    </Link>
                    ,&nbsp;
                    <Link
                        onClick={() => resourceClick(HLConst.powerPointPresPDF)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        summary powerpoint presentation
                    </Link>
                    ,&nbsp;
                    <Link
                        onClick={() => resourceClick(HLConst.overviewPDF)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        one page overview
                    </Link>
                    ,&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.videoOverview_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        short video overview
                    </Link>
                    , recording of a one hour overview, demonstration and Q&A&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.webinar_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        webinar
                    </Link>
                    &nbsp;and&nbsp;
                    <Link
                        onClick={() => resourceClick(HLConst.templatesXLSX)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        H-LiST templates file
                    </Link>
                    .
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Who developed H-LiST?
                </p>

                <p>
                    H-LiST was developed by the Center for Humanitarian Health at Johns Hopkins University, in
                    collaboration with the&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.LiST_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Lives Saved Tool (LiST)
                    </Link>
                    &nbsp;team and&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.UNHCR_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        UNHCR
                    </Link>
                    . It is supported by the&nbsp;
                    <Link
                        onClick={() => URL_Click(HLConst.Gates_URL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Bill & Melinda Gates Foundation
                    </Link>
                    .
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Still need help?
                </p>

                <p>
                    For more information please contact&nbsp;
                    <Link
                        href={"mailto:" + HLConst.humanitHealthMailLink}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        humanithealth@jhu.edu
                    </Link>
                    .
                </p>
            </div>
        );

        const creditsDiv = (
            <div>
                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Introduction page
                </p>

                <p>
                    2009-272, A woman carries a water container at Al Kharaz Refugee Camp, Yemen.
                    <br />© 2009 Micah Albert, Courtesy of Photoshare
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Step 4 page
                </p>

                <p>
                    Water and Sanitation Collaborative Council. <br></br>
                    Taken by Javier-Acebal. <br></br>
                    20160816-Cameroun20160816_camerun_5112-Javier-Acebal <br></br>
                    Refugee camp in Cameroun. <br></br>
                    Taken on August 16, 2016. <br></br>
                    <Link
                        onClick={() => URL_Click(HLConst.step4ImgURL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Link
                    </Link>
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Step 5 page
                </p>

                <p>
                    Tanya Martineau, Prospect Arts, Food for the Hungry. <br></br>
                    Taken on June 20, 2015. <br></br>
                    <Link
                        onClick={() => URL_Click(HLConst.step5ImgURL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Link
                    </Link>
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Step 6 page
                </p>

                <p>
                    Giacomo Cuscunà <br></br>
                    Bab al-Salam refugee camp - Syria - Unhcr <br></br>
                    Taken on March 6, 2013. <br></br>
                    <Link
                        onClick={() => URL_Click(HLConst.step6ImgURL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Link
                    </Link>
                </p>

                <p
                    style={{
                        color: Theme.HL_SecondaryColor,
                        ...Theme.labelFontStyle,
                        fontSize: 18,
                    }}
                >
                    Step 7 page
                </p>

                <p>
                    Water and Sanitation Collaborative Council <br></br>
                    Taken by Javier-Acebal. <br></br>
                    20160816-Cameroun20160816_camerun_9614-Javier-Acebal <br></br>
                    Refugee camp in Cameroun. <br></br>
                    Taken on August 16, 2016. <br></br>
                    <Link
                        onClick={() => URL_Click(HLConst.step7ImgURL)}
                        style={{
                            color: Theme.HL_LinkBlue,
                            cursor: "pointer",
                        }}
                        underline={"hover"}
                    >
                        Link
                    </Link>
                </p>
            </div>
        );

        const accordion = (
            <div
                style={{
                    marginBottom: 40,
                    marginLeft: Theme.leftIndent,
                    marginRight: Theme.leftIndent,
                    marginTop: 20,
                }}
            >
                <TAccordion
                    content={[
                        howItWorksDiv,
                        inclIntervsDiv,
                        dataReqDiv,
                        costCompDiv,
                        resultsDiv,
                        usesDiv,
                        limitationsDiv,
                        moreInfoDiv,
                        creditsDiv,
                    ]}
                    items={[
                        "How the H-LiST approach works",
                        "Included interventions",
                        "Data requirements",
                        "Costing component",
                        "Results",
                        "Uses",
                        "Limitations",
                        "More information",
                        "Credits",
                    ]}
                    key={"accordion"}
                    summaryStyle={{
                        color: Theme.HL_PrimaryColor,
                        fontFamily: Theme.fontFamily,
                        fontSize: 20,
                    }}
                />
            </div>
        );

        return accordion;
    };

    render() {
        const FAQsHelpText = (
            <p
                style={{
                    ...Theme.pageHeadingFontStyle,
                    marginLeft: Theme.leftIndent,
                }}
            >
                {HLConst.FAQsHelpAreaText}
            </p>
        );

        const FAQsDivider = (
            <Divider
                style={{
                    ...Theme.dividerStyle,
                    marginBottom: Theme.topIndent,
                    marginLeft: Theme.leftIndent,
                }}
            />
        );

        const FAQsLab = (
            <TLabel
                caption={"Click on a topic to view FAQs."}
                key={"FAQsLab"}
                style={{
                    ...Theme.labelFontStyle,
                    marginLeft: Theme.leftIndent,
                }}
            />
        );

        const FAQsAccordion = this.renderFAQsAccordion();

        return (
            <div
                style={{
                    minHeight: window.innerHeight - Theme.HL_FooterOffset, //Theme.HL_FooterPosition,
                }}
            >
                {FAQsHelpText}
                {FAQsDivider}
                {FAQsLab}
                {FAQsAccordion}
            </div>
        );
    }
}

export default HLFAQsHelpForm;
